import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Icon,
  ScrollView,
  ScrollViewItem,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { Destination } from '@/features/destinations/utils/Destination';
import { getDestinationRegionLabel } from '@/features/destinations/utils/DestinationRegionLabelMapper';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import useAppointmentStore from '@/store/AppointmentStore';
import {
  getRelevanceSearch,
  relevanceSearchQueryKey,
} from '../api/getRelevanceSearch';
import type { RelevanceSearchResult } from '../types/RelevanceSearchResult';

interface DestinationCardData {
  name: string;
  location: string;
  image: string;
  searchTerm: string;
}

const FEATURED_DESTINATIONS: DestinationCardData[] = [
  {
    name: 'Italy',
    location: 'Rome',
    searchTerm: 'Italy',
    image:
      'https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-683962492:cards-strd-600x400',
  },
  {
    name: 'France',
    location: 'Paris',
    searchTerm: 'France',
    image:
      'https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-1815364539:cards-strd-600x400',
  },
  {
    name: 'Hawaii',
    location: 'Oahu',
    searchTerm: 'Hawaii',
    image:
      'https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-1397681795:cards-strd-600x400',
  },
  {
    name: 'Mexico',
    location: 'Cancun',
    searchTerm: 'Mexico',
    image:
      'https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-1945140880:cards-strd-600x400',
  },
  {
    name: 'Caribbean',
    location: 'Aruba',
    searchTerm: 'Caribbean',
    image:
      'https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-103822101:cards-strd-600x400',
  },
  {
    name: 'Japan',
    location: 'Tokyo',
    searchTerm: 'Japan',
    image:
      'https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-1441083322:cards-strd-600x400',
  },
];

const POPULAR_DESTINATIONS: DestinationCardData[] = [
  {
    name: 'Canada',
    location: 'Banff',
    searchTerm: 'Canada',
    image:
      'https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-1599827836:cards-strd-600x400',
  },
  {
    name: 'Argentina',
    location: 'Patagonia',
    searchTerm: 'Argentina',
    image:
      'https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-2196380973:cards-strd-600x400',
  },
  {
    name: 'Portugal',
    location: 'Algarve',
    searchTerm: 'Portugal',
    image:
      'https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-639053494:cards-strd-600x400',
  },
  {
    name: 'Turkey',
    location: 'Cappadocia',
    searchTerm: 'Turkey',
    image:
      'https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-1050859762:cards-strd-600x400',
  },
  {
    name: 'Vietnam',
    location: 'Halong Bay',
    searchTerm: 'Vietnam',
    image:
      'https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-1417912195:cards-strd-600x400',
  },
  {
    name: 'Saudi Arabia',
    location: 'Hegra',
    searchTerm: 'Saudi Arabia',
    image:
      'https://assets.ace.aaa.com/is/image/aceaaa/GettyImages-2202043615:cards-strd-600x400',
  },
];

interface DestinationCardProps {
  destination: DestinationCardData;
  onClick: (destination: DestinationCardData) => void;
}

function DestinationCard({ destination, onClick }: DestinationCardProps) {
  return (
    <Card
      role='button'
      tabIndex={0}
      onClick={() => onClick(destination)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(destination);
        }
      }}
      sx={{
        position: 'relative',
        width: 200,
        height: 220,
        flexShrink: 0,
        borderRadius: 3,
        cursor: 'pointer',
        '&:hover': {
          transform: 'scale(1.02)',
          transition: 'transform 0.2s ease-in-out',
        },
      }}
      data-testid={`destination-card-${destination.name.toLowerCase().replace(/\s/g, '-')}`}
    >
      <CardMedia
        image={destination.image}
        title={destination.name}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
        }}
      />
      <CardContent
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          p: 1.5,
          '&:last-child': { pb: 1.5 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant='h5' sx={{ color: '#fff', lineHeight: 1.2 }}>
          {destination.name}
        </Typography>
        <Stack
          direction='row'
          spacing={0.5}
          alignItems='center'
          sx={{ mt: 0.5 }}
        >
          <Icon
            iconName='location-dot-duotone'
            sx={{ color: '#fff', width: 22, height: 24 }}
          />
          <Typography
            variant='b2Regular'
            sx={{
              color: '#fff',
              textShadow:
                '0 -2px 1px rgba(8, 17, 34, 0.04), 0 2px 4px rgba(8, 17, 34, 0.20)',
            }}
          >
            {destination.location}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

interface DestinationSectionProps {
  title: string;
  destinations: DestinationCardData[];
  onCardClick: (destination: DestinationCardData) => void;
}

function DestinationSection({
  title,
  destinations,
  onCardClick,
}: DestinationSectionProps) {
  return (
    <Box sx={{ width: '100%', maxWidth: 1280, mx: 'auto' }}>
      <Typography
        variant='h5Medium'
        sx={{
          color: '#0B3E6F',
          textShadow:
            '0 -2px 4px rgba(8, 17, 34, 0.04), 0 8px 16px rgba(8, 17, 34, 0.20)',
          mb: 2,
        }}
      >
        {title}
      </Typography>
      <ScrollView
        spacing='12px'
        scrollDistance={75}
        sx={{
          '& .MuiIconButton-root': {
            backgroundColor: '#016BCD',
            color: '#fff',
            width: 36,
            height: 36,
            zIndex: 3,
            display: { xs: 'none', md: 'flex' },
            '&:hover': { backgroundColor: '#0155a4' },
          },
        }}
      >
        {destinations.map((dest, index) => (
          <ScrollViewItem key={`${dest.name}-${index}`} sx={{ width: 'auto' }}>
            <DestinationCard destination={dest} onClick={onCardClick} />
          </ScrollViewItem>
        ))}
      </ScrollView>
    </Box>
  );
}

interface DestinationCardsProps {
  proceedWithDestination: (destination: Destination) => void;
}

export default function DestinationCards({
  proceedWithDestination,
}: DestinationCardsProps) {
  const destinations = useAppointmentStore((state) => state.destinations);
  const setSelectedDestination = useAppointmentStore(
    (state) => state.setSelectedDestination,
  );
  const setSearchedDestination = useAppointmentStore(
    (state) => state.setSearchedDestination,
  );
  const useNewDestinations = useFeatureFlag('useNewDestinations');
  const useGoogleApiForRBS = useFeatureFlag('useGoogleApiForRBS');
  const queryClient = useQueryClient();

  const handleCardClick = useCallback(
    async (card: DestinationCardData) => {
      let searchResults: RelevanceSearchResult[];
      try {
        searchResults = await queryClient.fetchQuery({
          queryKey: relevanceSearchQueryKey(
            card.searchTerm,
            useNewDestinations,
            useGoogleApiForRBS,
          ),
          queryFn: () =>
            getRelevanceSearch(
              card.searchTerm,
              useNewDestinations,
              useGoogleApiForRBS,
            ),
          staleTime: Number.POSITIVE_INFINITY,
        });
      } catch {
        return;
      }

      if (!searchResults.length) return;

      const topResult = searchResults[0];

      const matchedDestination = destinations.find(
        (dest) => dest.name === topResult.aceDestination,
      );

      if (!matchedDestination) return;

      const regionLabel = getDestinationRegionLabel(
        matchedDestination.name,
        useNewDestinations,
      );

      const mappedDestination: Destination = {
        ...matchedDestination,
        name: regionLabel,
      };

      setSelectedDestination(mappedDestination);
      setSearchedDestination(card.name);
      proceedWithDestination(mappedDestination);
    },
    [
      destinations,
      useNewDestinations,
      useGoogleApiForRBS,
      queryClient,
      setSelectedDestination,
      setSearchedDestination,
      proceedWithDestination,
    ],
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: { xs: 'auto', md: '731px' },
        px: { xs: 2, md: 4 },
        py: 4,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 4,
      }}
      data-testid='destination-cards-section'
    >
      <DestinationSection
        title='Featured destinations'
        destinations={FEATURED_DESTINATIONS}
        onCardClick={handleCardClick}
      />
      <DestinationSection
        title='Popular destinations'
        destinations={POPULAR_DESTINATIONS}
        onCardClick={handleCardClick}
      />
    </Box>
  );
}
